import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from '../api/axiosInstance';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

function formatLocalDateTime(input) {
    const dt = new Date(input);
    const offset = dt.getTimezoneOffset();
    const localDate = new Date(dt.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
}

function BloodSugarHistory() {
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [userName, setUserName] = useState("訪客");
    const [error, setError] = useState("");
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split("T")[0];
    });
    const [endDate, setEndDate] = useState(() =>
        new Date().toISOString().split("T")[0]
    );
    const [type, setType] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    const fetchRecords = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(
                `/api/BloodSugar?startDate=${startDate}&endDate=${endDate}&type=${type}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setRecords(res.data);
            setError("");
        } catch {
            setError("查詢失敗，請重新登入或稍後再試。");
        }
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        setEditData({
            recordedAt: formatLocalDateTime(record.recordedAt),
            glucose: record.glucose,
            notes: record.notes || "",
            type: record.type,
        });
    };

    const handleSave = async (id) => {
        if (!window.confirm("確定要儲存修改？")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.put(`/api/BloodSugar/${id}`, editData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEditingId(null);
            fetchRecords();
        } catch {
            alert("更新失敗！");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("確定要刪除這筆紀錄？")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`/api/BloodSugar/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchRecords();
        } catch {
            alert("刪除失敗！");
        }
    };

    useEffect(() => {
        document.title = "歷史血糖紀錄";
        fetchRecords();

        try {
            const user = JSON.parse(localStorage.getItem("user"));
            if (user?.name) {
                setUserName(user.name);
            }
        } catch (err) {
            console.error("讀取 user 發生錯誤", err);
        }
    }, []);

    return (
        <div className="container mt-4">
            <h2 className="mb-4">血糖紀錄 - {userName}</h2>

            <div className="mb-3">
                <button className="btn btn-success me-2" onClick={() => navigate("/blood-sugar/new")}>
                    新增血糖記錄
                </button>
                <button className="btn btn-outline-secondary" onClick={() => navigate("/dashboard")}>
                    回到目錄
                </button>
            </div>

            <div className="row g-2 align-items-end mb-3">
                <div className="col-auto">
                    <label className="form-label">起始日期</label>
                    <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                <div className="col-auto">
                    <label className="form-label">結束日期</label>
                    <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <div className="col-auto">
                    <label className="form-label">類別</label>
                    <select
                        className="form-control"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        <option value="">全部</option>
                        <option value={0}>空腹</option>
                        <option value={1}>飯後</option>
                    </select>
                </div>
                <div className="col-auto">
                    <button className="btn btn-primary" onClick={fetchRecords}>
                        查詢
                    </button>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {records.length > 0 && (
                <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer>
                        {/* <LineChart data={records}> */}
                        <LineChart data={[...records].reverse()}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="recordedAt"
                                tickFormatter={(value) =>
                                    new Date(value).toLocaleString("zh-TW", {
                                        month: "2-digit",
                                        day: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })
                                }
                            />
                            <YAxis />
                            <Tooltip
                                labelFormatter={(value) =>
                                    new Date(value).toLocaleString("zh-TW", {
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })
                                }
                            />
                            <Legend />
                            <Line type="monotone" dataKey="glucose" stroke="#2ecc71" name="血糖值" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            <div className="table-responsive">
                <table className="table table-bordered table-striped table-hover">
                    <thead className="table-light">
                        <tr>
                            <th>日期時間</th>
                            <th>血糖值</th>
                            <th>類別</th>
                            <th>備註</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="text-center">尚無資料</td>
                            </tr>
                        ) : (
                            records.map((r) =>
                                editingId === r.id ? (
                                    <tr key={r.id}>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={editData.recordedAt}
                                            onChange={(e) => setEditData({ ...editData, recordedAt: e.target.value })}
                                        />
                                        <td><input className="form-control" value={editData.glucose} onChange={(e) => setEditData({ ...editData, glucose: e.target.value })} /></td>
                                        <td>
                                            <select
                                                className="form-control"
                                                value={editData.type}
                                                onChange={(e) => setEditData({ ...editData, type: parseInt(e.target.value) })}
                                            >
                                                <option value={0}>空腹</option>
                                                <option value={1}>飯後</option>
                                            </select>
                                        </td>
                                        <td><input className="form-control" value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} /></td>
                                        <td>
                                            <button className="btn btn-success btn-sm me-1" onClick={() => handleSave(r.id)}>儲存</button>
                                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>取消</button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={r.id}>
                                        <td>{new Date(r.recordedAt).toLocaleString("zh-TW", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit"
                                        })}</td>
                                        <td>{r.glucose}</td>
                                        <td>{r.type === 0 ? "空腹" : "飯後"}</td>
                                        <td>{r.notes}</td>
                                        <td>
                                            {r.source === r.userId && (
                                                <>
                                                    <button className="btn btn-outline-primary btn-sm me-1" onClick={() => handleEdit(r)}>編輯</button>
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(r.id)}>刪除</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                )
                            )
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default BloodSugarHistory;
